import { createContext, use, useMemo, useState, type ReactNode } from 'react';
import { callFunction } from '../lib/callables';
import { clearStoredSession, getStoredSession, setStoredSession, type SessionRole, type StoredSession } from '../lib/session';

interface ArenaUser {
  id: string;
  role: SessionRole;
}

interface AuthValue {
  user: ArenaUser | null;
  loading: boolean;
  isAdmin: boolean;
  teamId: string | null;
  loginWithTeamCode: (code: string) => Promise<void>;
  loginAsAdmin: (code: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StoredSession | null>(() => getStoredSession());

  const user = useMemo<ArenaUser | null>(() => (session ? { id: session.teamId ?? 'admin', role: session.role } : null), [session]);
  const isAdmin = session?.role === 'admin';
  const teamId = session?.role === 'team' ? session.teamId : null;

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading: false,
      isAdmin,
      teamId,
      loginWithTeamCode: async (code: string) => {
        const result = await callFunction<{ code: string }, { token: string; role: 'team'; teamId: string }>('redeemTeamCode', { code });
        const nextSession = { token: result.token, role: result.role, teamId: result.teamId };
        setStoredSession(nextSession);
        setSession(nextSession);
      },
      loginAsAdmin: async (code: string) => {
        const result = await callFunction<{ code: string }, { token: string; role: 'admin' }>('adminLogin', { code });
        const nextSession = { token: result.token, role: result.role, teamId: null };
        setStoredSession(nextSession);
        setSession(nextSession);
      },
      logout: () => {
        clearStoredSession();
        setSession(null);
      },
    }),
    [isAdmin, teamId, user],
  );
  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth() {
  const context = use(AuthContext);
  if (!context) throw new Error('AuthProvider is missing');
  return context;
}
